import React, { RefObject, useState } from 'react';
import { toBlob } from 'html-to-image';
import { CheckIcon, DownloadIcon, SpinnerIcon } from './ActionIcons';

interface ResultImageExportProps {
    target: RefObject<HTMLElement>;
    fileName: string;
}

/** Marks the export root while capturing: unclips scroll containers so the whole table is in frame. */
const EXPORTING_CLASS = 'exporting-image';
const EXPORT_BACKGROUND = '#1a1a1a';
/** Must match `.exporting-image` padding and the unclipped selectors in index.css. */
const EXPORT_PADDING_PX = 16;
const SCROLL_CONTAINER_SELECTOR = ".table-container, .consume-grid-wrap, [style*='overflow']";
/** Stay well under browser canvas limits (~16k px per side, ~268M px area). */
const MAX_CANVAS_SIDE = 16000;
const MAX_CANVAS_AREA = 120_000_000;

function exportPixelRatio(width: number, height: number): number {
    const bySide = MAX_CANVAS_SIDE / Math.max(width, height);
    const byArea = Math.sqrt(MAX_CANVAS_AREA / (width * height));
    return Math.max(0.5, Math.min(2, bySide, byArea));
}

/** 1×1 transparent GIF, used if an image still cannot be embedded. */
const IMAGE_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const IMAGE_FETCH_CONCURRENCY = 6;
/** Data URLs by source URL, shared across exports. Holds promises so concurrent lookups share one fetch. */
const dataUrlCache = new Map<string, Promise<string | null>>();

function fetchAsDataUrl(url: string): Promise<string | null> {
    let pending = dataUrlCache.get(url);
    if (!pending) {
        pending = fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.blob();
            })
            .then(
                (blob) =>
                    new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(blob);
                    })
            )
            .catch((err) => {
                console.warn('Image export: could not embed', url, err);
                dataUrlCache.delete(url);
                return null;
            });
        dataUrlCache.set(url, pending);
    }
    return pending;
}

/**
 * html-to-image fetches every <img> itself and only caches after a fetch completes, so a grid of
 * thousands of icons fires thousands of parallel requests (ERR_INSUFFICIENT_RESOURCES).
 * Instead: fetch each distinct URL once with bounded concurrency, swap the live srcs to data URLs
 * (which the library skips), and return a function that restores the originals.
 */
async function inlineImages(element: HTMLElement): Promise<() => void> {
    const images = Array.from(element.querySelectorAll('img')).filter(
        (img) => img.src.length > 0 && !img.src.startsWith('data:')
    );
    const urls = Array.from(new Set(images.map((img) => img.src)));
    const dataUrls = new Map<string, string>();
    let next = 0;
    const worker = async () => {
        while (next < urls.length) {
            const url = urls[next];
            next += 1;
            const dataUrl = await fetchAsDataUrl(url);
            if (dataUrl) {
                dataUrls.set(url, dataUrl);
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(IMAGE_FETCH_CONCURRENCY, urls.length) }, worker));

    const swapped: Array<{ img: HTMLImageElement; src: string }> = [];
    for (const img of images) {
        const dataUrl = dataUrls.get(img.src);
        if (dataUrl) {
            swapped.push({ img, src: img.getAttribute('src') ?? img.src });
            img.src = dataUrl;
        }
    }
    await Promise.all(swapped.map(({ img }) => img.decode().catch(() => undefined)));

    return () => {
        for (const { img, src } of swapped) {
            img.src = src;
        }
    };
}

async function captureElement(element: HTMLElement): Promise<Blob> {
    const restoreImages = await inlineImages(element);
    // Hug the content (max-content: a table at its natural width), but never wider than the on-screen
    // element plus what its scroll containers hide; max-content alone would put every paragraph on one line.
    const hiddenOverflow = Array.from(element.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTOR)).reduce(
        (widest, scroller) => Math.max(widest, scroller.scrollWidth - scroller.clientWidth),
        0
    );
    const maxWidth = element.clientWidth + hiddenOverflow + 2 * EXPORT_PADDING_PX;
    element.classList.add(EXPORTING_CLASS);
    const previousWidth = element.style.width;
    element.style.width = 'max-content';
    const width = Math.ceil(Math.min(element.offsetWidth, maxWidth));
    element.style.width = `${width}px`;
    try {
        const height = element.scrollHeight;
        const blob = await toBlob(element, {
            width,
            height,
            backgroundColor: EXPORT_BACKGROUND,
            pixelRatio: exportPixelRatio(width, height),
            imagePlaceholder: IMAGE_PLACEHOLDER,
            filter: (node) => !(node instanceof HTMLElement && node.dataset.exportIgnore !== undefined),
        });
        if (!blob) {
            throw new Error('Empty image');
        }
        return blob;
    } finally {
        element.style.width = previousWidth;
        element.classList.remove(EXPORTING_CLASS);
        restoreImages();
    }
}

function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const DONE_FLASH_MS = 1500;

const ResultImageExport: React.FC<ResultImageExportProps> = ({ target, fileName }) => {
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [failed, setFailed] = useState(false);

    const save = async () => {
        if (!target.current || busy) {
            return;
        }
        setBusy(true);
        setFailed(false);
        try {
            downloadBlob(await captureElement(target.current), fileName);
            setDone(true);
            window.setTimeout(() => setDone(false), DONE_FLASH_MS);
        } catch (err) {
            console.error('Image export failed:', err);
            setFailed(true);
        } finally {
            setBusy(false);
        }
    };

    const label = failed ? 'Export failed — try again' : busy ? 'Rendering image…' : 'Save as image';
    return (
        <button
            type="button"
            className={`icon-button${failed ? ' is-error' : ''}`}
            title={label}
            aria-label={label}
            disabled={busy}
            onClick={() => void save()}
        >
            {busy ? <SpinnerIcon /> : done ? <CheckIcon /> : <DownloadIcon />}
        </button>
    );
};

export default ResultImageExport;
